import { useState } from "react";
import { MapPin, Phone, Mail, MessageCircle, Clock } from "lucide-react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import SEOHead from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { FloatingWhatsApp } from "@/components/ui/floating-whatsapp";
import { generateContactWhatsAppLink } from "@/utils/whatsapp";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const whatsappMessage = `Hola, soy ${formData.name}.
Email: ${formData.email}
Mensaje: ${formData.message}`;

    const link = generateContactWhatsAppLink(whatsappMessage);
    window.open(link, "_blank");
    toast.success("Redirigiendo a WhatsApp...");
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleWhatsAppDirect = () => {
    const link = generateContactWhatsAppLink("Hola, me gustaría hacer una consulta.");
    window.open(link, "_blank");
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: "Dirección",
      value: "San José, Costa Rica",
    },
    {
      icon: Phone,
      title: "Teléfono",
      value: "+506 0000 0000",
    },
    {
      icon: Mail,
      title: "Email",
      value: "info@tekton.cr",
    },
  ];

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SEOHead
        title="Contacto"
        description="Contactanos para consultas sobre productos tecnológicos. WhatsApp, teléfono y formulario de contacto disponibles. Atención personalizada en San José, Costa Rica."
        keywords="contacto, tienda tecnología, WhatsApp, Costa Rica, San José, Tekton"
      />
      <Header />

      <main className="flex-1">
        {/* Hero Section */}
        <div className="bg-primary text-white py-12">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-3xl md:text-4xl font-bold mb-2">Contactanos</h1>
            <p className="text-white/80">
              ¿Tenés alguna consulta? Estamos aquí para ayudarte
            </p>
          </div>
        </div>

        <section className="py-12">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {/* Contact Form */}
              <div className="bg-card rounded-lg border border-border p-6 md:p-8">
                <h2 className="text-xl font-bold mb-6">Envianos un Mensaje</h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium mb-2">
                      Nombre *
                    </label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Tu nombre"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium mb-2">
                      Email *
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="tu@email.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium mb-2">
                      Mensaje *
                    </label>
                    <Textarea
                      id="message"
                      name="message"
                      required
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Contanos en qué podemos ayudarte..."
                      rows={5}
                      className="resize-none"
                    />
                  </div>

                  <Button type="submit" size="lg" variant="cta" className="w-full">
                    <MessageCircle className="mr-2" />
                    Enviar por WhatsApp
                  </Button>
                </form>
              </div>

              {/* Contact Info */}
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-bold mb-4">Información de Contacto</h2>
                  <div className="space-y-3">
                    {contactInfo.map((info) => (
                      <div 
                        key={info.title}
                        className="flex items-start gap-4 p-4 bg-muted rounded-lg border border-border"
                      >
                        <div className="w-10 h-10 rounded-lg gradient-primary flex items-center justify-center flex-shrink-0">
                          <info.icon className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-sm">{info.title}</h3>
                          <p className="text-muted-foreground">{info.value}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* WhatsApp CTA */}
                <div className="bg-accent/10 border border-accent/20 rounded-lg p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-[hsl(142,70%,45%)] flex items-center justify-center flex-shrink-0">
                      <MessageCircle className="h-5 w-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold mb-1">¿Preferís WhatsApp?</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Hablá directamente con nosotros para una respuesta más rápida
                      </p>
                      <Button 
                        onClick={handleWhatsAppDirect} 
                        className="w-full bg-[hsl(142,70%,45%)] hover:bg-[hsl(142,70%,40%)] text-white"
                      >
                        <MessageCircle className="mr-2 h-4 w-4" />
                        Abrir WhatsApp
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Business Hours */}
                <div className="bg-muted rounded-lg border border-border p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Clock className="h-5 w-5 text-primary" />
                    <h3 className="font-bold">Horarios de Atención</h3>
                  </div>
                  <div className="space-y-2 text-sm">
                    <p className="flex justify-between">
                      <span className="text-muted-foreground">Lunes a Viernes</span>
                      <span className="font-medium">9:00 - 18:00</span>
                    </p>
                    <p className="flex justify-between">
                      <span className="text-muted-foreground">Sábados</span>
                      <span className="font-medium">9:00 - 13:00</span>
                    </p>
                    <p className="flex justify-between">
                      <span className="text-muted-foreground">Domingos</span>
                      <span className="font-medium">Cerrado</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <FloatingWhatsApp />
    </div>
  );
};

export default Contact;
